import React, { useCallback, useEffect, useRef, useState } from "react";

import PostAddIcon from "@mui/icons-material/PostAdd";
import TodayOutlinedIcon from "@mui/icons-material/TodayOutlined";
import { Box, Button, CircularProgress, Divider, FormControlLabel, Stack, Switch } from "@mui/material";
import EmptyCardAnalytics from "assets/images/EmptyCardAnalytics.png";
import { EmptyCard } from "components/Empty-card/EmptyCard";
// import { SaveView } from "components/popups";
import { SelectDateRange } from "components/forms";
import { DealRoleGuard } from "components/guards";
import {
  ContextMenuReports,
  DealGraph,
  DealHedgeFundTable,
  InfoCard,
  MeasuresTable,
  RisksTable
} from "components/reports";
import { EMPTY_CARD_CONTENT } from "constants/empty-card";
import { staticReportsQueries } from "constants/reports";
import { useActionHook, useDidUpdate, useModals, useQueryHook } from "hooks";
import { isNull } from "lodash";
import {
  useAccountSelector,
  useDealIdSelector,
  useDealNullifyCommitmentsSelect,
  useDealSubAssetsSelector,
  useDealTitleSelector,
  useIsHedgeFundSelect,
  useRefetchReportsSelect,
  useReportsActions,
  useReportsQueriesSelector,
  useReportsTableSelector,
  useViewActions
} from "store";
import { DateRangeValue } from "store/types";
import SyncTasker from "utils/sync-tasker";

import { SelectCurrency } from "../../components/reports/Select-currency/SelectCurrency";
import { RoleName } from "../../constants";

const isInTheFuture = (date: string): boolean => {
  const today = new Date();
  today.setHours(23, 59, 59, 998);
  return new Date(date) > today;
};

const syncTasker = new SyncTasker(300);
const riskSyncTasker = new SyncTasker(300);
const graphSyncTasker = new SyncTasker(300);
const tableSyncTasker = new SyncTasker(300);

export const DealReport: React.FC = () => {
  const queries = useReportsQueriesSelector();
  const refetchReport = useRefetchReportsSelect();
  const isNullifyCommitments = useDealNullifyCommitmentsSelect();

  const dealTitle = useDealTitleSelector();
  const dealId = useDealIdSelector();
  const subAssets = useDealSubAssetsSelector();
  const { hedge_fund } = useAccountSelector() || {};
  const isHedgeFundOn = useIsHedgeFundSelect();

  const measuresData = useReportsTableSelector();
  const { setCreateManyMeasureData, setIsDealCharBarOpenAction } = useViewActions();

  const [showChildren, setShowChildren] = useState(false);

  const {
    dateRange,
    currency,
    calculatedFields,
    dealTableColumnBy,
    measures,
    dealTableGroupBy,
    dealTableRowBy,
    mainParameter,
    secondaryParameter,
    interval,
    filteredPortfolio,
    filters,
    exchangeMethod,
    cumulative,
    characteristics,
    cubicDeal,
    indexName,
    use_accounting_date
  } = queries;

  const actualDateRef = useRef<DateRangeValue<string>>(dateRange);

  const {
    setQueriesAction,
    getCubicAction,
    getMeasureGraphAction,
    getRiskMetricsAction,
    getReportTableAction,
    clearTableDataAction,
    getMetaAction,
    refetchReportAction,
    setIsHedgeFundsAction
  } = useReportsActions();

  const { noticeModal, errorModal } = useModals();

  const {
    data: cubicData,
    action: getCubic,
    loading: cubicLoading //pageType: cubicSnapshot, cubicDeal
  } = useActionHook(getCubicAction);

  const {
    refetch: refetchRisk,
    loading: isRiskLoading,
    data: risksTable
  } = useQueryHook(() =>
    getRiskMetricsAction({
      dateRange,
      measures,
      calculatedFields,
      currency,
      characteristics,
      exchangeMethod,
      filteredPortfolio,
      filters,
      indexName,
      use_accounting_date,
      dealTitles: showChildren ? [dealTitle!, ...subAssets.map((asset) => asset.title)] : [dealTitle!],
      ...(dateRange.value === "inceptionToLastNAV" && { force_navs_date: true })
    })
  );

  const { refetch, loading: isDataLoading } = useQueryHook(() =>
    getReportTableAction({
      dateRange,
      measures,
      characteristics,
      currency,
      rowBy: dealTableRowBy,
      groupBy: dealTableGroupBy,
      columnBy: dealTableColumnBy,
      calculatedFields,
      exchangeMethod,
      use_accounting_date,
      filters: {
        deal_name: showChildren ? [dealTitle!, ...subAssets.map((asset) => asset.title)] : [dealTitle!]
      },
      originalCurrency: false,
      columns: [],
      ...(dateRange.value === "inceptionToLastNAV" && { force_navs_date: true })
    })
  );

  const {
    action: fetchGraph,
    loading: isGraphLoading,
    data: dealGraph
  } = useActionHook(() => {
    const subAssetNames = subAssets.map((asset) => asset.title);

    return getMeasureGraphAction({
      withinDeal: false,
      filters: { deal_name: showChildren ? [dealTitle!, ...subAssetNames] : [dealTitle!] },
      dateRange,
      mainParameter,
      secondaryParameter,
      interval,
      currency,
      exchangeMethod,
      cumulative,
      use_accounting_date,
      groupByGraph: null,
      columnType: "stacked"
    });
  });

  useEffect(() => {
    if (Object.keys(cubicDeal).length && dealTitle) {
      const subAssetNames = subAssets.map((asset) => asset.title);
      getCubic({
        cubics: cubicDeal,
        pageType: "cubicDeal",
        filters: { deal_name: showChildren ? [dealTitle, ...subAssetNames] : [dealTitle] },
        exchangeMethod,
        currency,
        dateRange,
        use_accounting_date
      });
    }
  }, [dealId, cubicDeal]);

  const refetchData = (withCubics = true, withGraph = true) => {
    const subAssetNames = subAssets.map((asset) => asset.title);
    refetch();
    refetchRisk();
    if (withGraph) {
      fetchGraph();
    }
    if (withCubics && Object.keys(cubicDeal).length && dealTitle) {
      getCubic({
        cubics: cubicDeal,
        pageType: "cubicDeal",
        filters: { deal_name: showChildren ? [dealTitle, ...subAssetNames] : [dealTitle] },
        exchangeMethod,
        currency,
        dateRange,
        use_accounting_date
      });
    }
    !isNull(refetchReport) && refetchReportAction(null); // Disabled refetch if it was not null after refetch
  };

  useDidUpdate(() => {
    // Refetch when a file was uploaded
    if (refetchReport === "deal") {
      refetchData();
    }
  }, [refetchReport]);

  useDidUpdate(() => {
    syncTasker.addTask(() => refetchData(true, false));
    return () => syncTasker.abortTasks();
  }, [dealId, isNullifyCommitments]);

  useDidUpdate(() => {
    syncTasker.addTask(refetchData);
    return () => syncTasker.abortTasks();
  }, [currency, dateRange, showChildren, exchangeMethod]);

  useEffect(() => {
    graphSyncTasker.addTask(fetchGraph);
    return () => graphSyncTasker.abortTasks();
  }, [mainParameter, secondaryParameter, interval, dateRange, dealId, dealTitle, exchangeMethod, isNullifyCommitments]);

  useDidUpdate(() => {
    riskSyncTasker.addTask(refetchRisk);
    return () => riskSyncTasker.abortTasks();
  }, [measures, calculatedFields, characteristics, filteredPortfolio, dateRange, filters, indexName]);

  useDidUpdate(() => {
    tableSyncTasker.addTask(refetch);
    return () => tableSyncTasker.abortTasks();
  }, [measures, dealTableGroupBy, dealTableColumnBy, dealTableRowBy, dateRange]);

  const handleDateRangeChange = useCallback((data: DateRangeValue, use_accounting_date?: boolean) => {
    if ((data.to && isInTheFuture(data.to)) || (data.from && isInTheFuture(data.from))) {
      noticeModal({
        msg: "Notice - Report can not show future values of different currency. Different currency's in future dates are ignored."
      });
    }

    if (data.to && data.from && data.from > data.to) {
      // eslint-disable-next-line quotes
      return errorModal({ msg: '"Date From" must be less than "Date To"' });
    }

    setQueriesAction({ dateRange: data, use_accounting_date });
  }, []);

  const setIndexName = (value: string | undefined) => {
    setQueriesAction({ indexName: value });
  };

  // Record date each time to ref.
  useEffect(() => {
    actualDateRef.current = dateRange;
  }, [dateRange]);

  useEffect(() => {
    clearTableDataAction();
    getMetaAction();

    return () => {
      // ref value will be all the time relevant
      if (actualDateRef.current.value !== "inceptionToLastNAV") return;
      setQueriesAction({ dateRange: staticReportsQueries.dateRange }); // Set Date tp default
    };
  }, []);

  const handleAddValue = useCallback(() => {
    if (!dealId) {
      return;
    }

    setCreateManyMeasureData({ dealId, dealTitle: dealTitle! });
  }, [setCreateManyMeasureData, dealId, dealTitle]);

  const isTablesLoading = isRiskLoading || isDataLoading;
  const hasActiveFilters = dateRange.value !== "fromInception" || currency !== "USD";
  const isTablesEmpty = !isTablesLoading && measuresData !== null && !measuresData.rows?.length && !hasActiveFilters;

  useEffect(() => {
    if (isTablesLoading || measuresData === null) return;
    setIsDealCharBarOpenAction(!isTablesEmpty);
  }, [isTablesEmpty]);

  return (
    <Stack
      sx={{
        gap: "22px",
        flexGrow: 1,
        backgroundColor: "white",
        borderRadius: "8px",
        padding: "22px",
        boxShadow: "0 1px 2px 0 #1212171a",
        margin: "0 2px 2px 2px",
        overflow: "auto"
      }}
    >
      {isTablesLoading ? (
        <Stack alignItems="center" justifyContent="center" sx={{ flexGrow: 1 }}>
          <CircularProgress />
        </Stack>
      ) : isTablesEmpty ? (
        <EmptyCard
          title={EMPTY_CARD_CONTENT.dealReport.title}
          description={{
            text: EMPTY_CARD_CONTENT.dealReport.description,
            externalText: EMPTY_CARD_CONTENT.dealReport.externalDescription
          }}
          image={{
            src: EmptyCardAnalytics,
            sx: { width: 315, height: 140 }
          }}
          fitPage
          actionsElement={
            <Button
              variant="contained"
              onClick={handleAddValue}
              startIcon={<PostAddIcon />}
              sx={{ bgcolor: "#071F35" }}
            >
              Add Value
            </Button>
          }
        />
      ) : (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              height: "50px"
            }}
          >
            <SelectDateRange
              formControlProps={{ sx: { minWidth: "200px" } }}
              selectProps={{
                startAdornment: <TodayOutlinedIcon sx={{ fontSize: "15px" }} />
              }}
              label=""
              value={dateRange}
              onChange={handleDateRangeChange}
              enableInceptionToLastNAV
            />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "14px"
              }}
            >
              {hedge_fund && (
                <FormControlLabel
                  labelPlacement="start"
                  control={<Switch checked={isHedgeFundOn} onChange={(e) => setIsHedgeFundsAction(e.target.checked)} />}
                  label="Hedge Fund View"
                />
              )}
              <Divider
                orientation="vertical"
                sx={{
                  backgroundColor: "#dae0e2",
                  width: "1px",
                  height: "30px"
                }}
              />
              {Boolean(subAssets.length) && (
                <FormControlLabel
                  control={<Switch checked={showChildren} onChange={(_, checked) => setShowChildren(checked)} />}
                  label="Show Children"
                />
              )}
              <SelectCurrency />
              <Divider
                orientation="vertical"
                sx={{
                  backgroundColor: "#dae0e2",
                  width: "1px",
                  height: "30px"
                }}
              />
              {/* <SaveView target="reports" type="report" currentLocation={location.pathname} />
          <Divider 
            orientation="vertical" 
            sx={{ 
              backgroundColor: "#dae0e2",
              width: "1px",
              height: "30px"
            }} 
          /> */}
              <DealRoleGuard availableRoles={[RoleName.Admin, RoleName.Analyst]}>
                <ContextMenuReports />
              </DealRoleGuard>
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "14px"
            }}
          >
            {cubicData &&
              Object.entries(cubicData).map(([name, data], index) => (
                <InfoCard pageType="cubicDeal" isLoading={cubicLoading} name={name} {...data} key={index} />
              ))}
          </Box>
          <DealGraph loading={isGraphLoading} graphData={dealGraph} />
          {isHedgeFundOn && dealId && <DealHedgeFundTable dealId={dealId} />}
          <RisksTable
            isLoading={isRiskLoading}
            showGroupBy={false}
            showRowBy={false}
            data={risksTable}
            widthTitle
            showRiskMetrics
            showCharacteristics
            selectedIndex={indexName}
            setSelectedIndex={setIndexName}
          />
          <MeasuresTable
            title="Deal Measures"
            isLoading={isDataLoading}
            isDealView={true}
            showOriginalCurrency={false}
            data={measuresData}
          />
        </>
      )}
    </Stack>
  );
};
